import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { ScaffoldModule } from "./scaffold/scaffold.module";
import { LoginComponent } from "./account/login/login.component";
import { CookieService } from "ngx-cookie-service";
import { MomentModule } from "ngx-moment";
import { HttpClient, HttpClientModule } from "@angular/common/http";
import { SharedModule } from "@app/shared/shared.module";
import { ResetPasswordComponent } from "./account/reset-password/reset-password.component";
import { MustHaveUpercaseDirective } from "./validators/must-have-upercase.directive";
import { MustHaveLowercaseDirective } from "./validators/must-have-lowercase.directive";
import { MustHaveNumberDirective } from "./validators/must-have-number.directive";
import { MustContainCharactersDirective } from "./validators/must-contain-characters.directive";
import { MustMatchInputDirective } from "./validators/must-match-input.directive";
import { ForgotPasswordComponent } from "./account/forgot-password/forgot-password.component";
import { ProfileComponent } from "./account/profile/profile.component";
import { NewPasswordRequiredComponent } from "./account/new-password-required/new-password-required.component";
import { UpdateAssetsComponent } from "./admin/admin-console/update-assets/update-assets.component";
import { ModelTypeListComponent } from "./data-model/type-list/type-list.component";
import { ModelKeysComponent } from "./data-model/model-keys/model-keys.component";
import { ConfirmDialog } from "./generic/confirm-dialog/confirm-dialog.component";
import { NewDataModelComponent } from "./data-model/new-data-model/new-data-model.component";
import { ModelDetailsComponent } from "./data-model/model-details/model-details.component";
import { ModelModesComponent } from "./data-model/model-modes/model-modes.component";
import { IntegrationsComponent } from "./admin/admin-console/integrations/integrations.component";
import { KeyItemsComponent } from "./data-model/key-items/key-items.component";
import { AdminConsoleComponent } from "./admin/admin-console/admin-console.component";
import { MinValueDirective } from "./validators/min-value.directive";
import { MaxValueDirective } from "./validators/max-value.directive";
import { PasswordPolicyComponent } from "./admin/admin-console/password-policy/password-policy.component";
import { LmsOptionsComponent } from "./admin/admin-console/lms-options/lms-options.component";
import { CourseImportComponent } from "./admin/admin-console/course-import/course-import.component";
import { VerifyPhoneAndEmailComponent } from "./account/verify-phone-and-email/verify-phone-and-email.component";
import { PwResetAndRefreshComponent } from "./admin/admin-console/pw-reset-and-refresh/pw-reset-and-refresh.component";
import { ValidUrlDirective } from "./validators/valid-url.directive";
import { NoWhiteSpaceDirective } from "./validators/no-white-space.directive";
import { DataModelPrefsComponent } from "./data-model/data-model-prefs/data-model-prefs.component";
import { DataConfigUpdateComponent } from "./data-model/data-config-update/data-config-update.component";
import { CatConfigComponent } from "./data-model/cat-config/cat-config.component";
import { NewDataCategoryComponent } from "./data-model/new-data-category/new-data-category.component";
import { TypeConfigComponent } from "./data-model/type-config/type-config.component";
import { PendingChangesGuard } from "@app/shared/pending-changes.guard";
import { SaveDialogComponent } from "./generic/save-dialog/save-dialog.component";
import { LtiCallbackComponent } from "./utility/lti-callback/lti-callback.component";
import { LtiFrameComponent } from "./utility/lti-frame/lti-frame.component";
import { NewUserEmailComponent } from "./admin/new-user-email/new-user-email.component";
import { ScaffoldDataExportComponent } from "./admin/admin-console/scaffold-data-export/scaffold-data-export.component";
import {
  NbThemeModule,
  NbLayoutModule,
  NbMenuModule,
  NbSidebarModule,
  NbDialogModule,
  NbDatepickerModule,
  NbToastrModule,
  NbTimepickerModule,
  NbTreeGridModule,
} from "@nebular/theme";
import { NbEvaIconsModule } from "@nebular/eva-icons";
import { ChangelogComponent } from "./changes/changelog/changelog.component";
import { CommentSidebarComponent } from "./commenting/comment-sidebar/comment-sidebar.component";
import { NewDataComponent } from "@scaffold/routes/new-data/new-data.component";
import { SecurityComponent } from './security/security.component';
import { TypeSelectComponent } from './account/type-select/type-select.component';
import { SlackConfigComponent } from "./admin/admin-console/slack-config/slack-config.component";
import {
  PasswordStrengthMeterModule,
  PSM_CONFIG,
} from "angular-password-strength-meter";
import * as zxcvbnCommonPackage from "@zxcvbn-ts/language-common";
import zxcvbnEnPackage from "@zxcvbn-ts/language-en";
import "./utility/update-password-matchers";
import { QrCodeComponent } from "./generic/qr-code/qr-code.component";
import { MfaModalComponent } from "./account/mfa-modal/mfa-modal.component";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { EnvironmentService } from "@services/environment.service";
import { OverlayComponent } from "./generic/overlay/overlay.component";

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
import { WizardModalComponent } from "./wizard-setup/wizard-modal/wizard-modal.component";
import { WizardIntegrationsComponent } from "./wizard-setup/wizard-integrations/wizard-integrations.component";
import { WizardThemeComponent } from "./wizard-setup/wizard-theme/wizard-theme.component";
import { WizardUsersComponent } from "./wizard-setup/wizard-users/wizard-users.component";
import { WizardIntegrationCreateComponent } from "./wizard-setup/wizard-integrations/wizard-integration-create/wizard-integration-create.component";
import { WizardIntegrationsListComponent } from "./wizard-setup/wizard-integrations/wizard-integrations-list/wizard-integrations-list.component";
import { WizardCheckToCloseModalComponent } from "./wizard-setup/wizard-check-to-close-modal/wizard-check-to-close-modal.component";
import { WizardIntegrationListItemComponent } from "./wizard-setup/wizard-integrations/wizard-integration-list-item/wizard-integration-list-item.component";
import { WizardTermsComponent } from "./wizard-setup/wizard-terms/wizard-terms.component";
import { GenericModule } from "@generic/generic.module";
import { WizardImportComponent } from './wizard-setup/wizard-import/wizard-import.component';
import { WizardCourseCodeComponent } from './wizard-setup/wizard-course-code/wizard-course-code.component';
import { WizardDateRangeComponent } from './wizard-setup/wizard-date-range/wizard-date-range.component';
import { NumberDirective } from './validators/number.directive';
import { ChoiceDialogComponent } from './generic/choice-dialog/choice-dialog.component';

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ResetPasswordComponent,
    MustHaveUpercaseDirective,
    MustHaveLowercaseDirective,
    MustHaveNumberDirective,
    MustContainCharactersDirective,
    MustMatchInputDirective,
    ForgotPasswordComponent,
    ProfileComponent,
    NewPasswordRequiredComponent,
    UpdateAssetsComponent,
    ModelTypeListComponent,
    ModelKeysComponent,
    ConfirmDialog,
    NewDataModelComponent,
    ModelDetailsComponent,
    ModelModesComponent,
    NewDataComponent,
    IntegrationsComponent,
    KeyItemsComponent,
    AdminConsoleComponent,
    MinValueDirective,
    MaxValueDirective,
    PasswordPolicyComponent,
    LmsOptionsComponent,
    CourseImportComponent,
    VerifyPhoneAndEmailComponent,
    PwResetAndRefreshComponent,
    ValidUrlDirective,
    NoWhiteSpaceDirective,
    DataModelPrefsComponent,
    CatConfigComponent,
    NewDataCategoryComponent,
    TypeConfigComponent,
    SaveDialogComponent,
    LtiCallbackComponent,
    NewUserEmailComponent,
    LtiFrameComponent,
    ChangelogComponent,
    ScaffoldDataExportComponent,
    CommentSidebarComponent,
    SlackConfigComponent,
    SecurityComponent,
    TypeSelectComponent,
    
    DataConfigUpdateComponent,
    QrCodeComponent,
    MfaModalComponent,
    OverlayComponent,
    WizardModalComponent,
    WizardIntegrationsComponent,
    WizardThemeComponent,
    WizardUsersComponent,
    WizardIntegrationCreateComponent,
    WizardIntegrationsListComponent,
    WizardCheckToCloseModalComponent,
    WizardIntegrationListItemComponent,
    WizardTermsComponent,
    WizardImportComponent,
    WizardCourseCodeComponent,
    WizardDateRangeComponent,
    NumberDirective,
    ChoiceDialogComponent
  ],
  imports: [
    GenericModule,
    BrowserModule,
    FormsModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    ScaffoldModule,
    MomentModule,
    HttpClientModule,
    SharedModule,
    ReactiveFormsModule,
    // LoadingBarHttpClientModule,
    // LoadingBarModule.forRoot(),
    NbThemeModule.forRoot({ name: "default" }),
    NbMenuModule.forRoot(),
    NbSidebarModule.forRoot(),
    NbDialogModule.forRoot(),
    NbDatepickerModule.forRoot(),
    NbTimepickerModule.forRoot(),
    NbToastrModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        // useClass: TranslationService,
        deps: [HttpClient, EnvironmentService],
      },
    }),
    NbLayoutModule,
    NbEvaIconsModule,
    NbTreeGridModule,
    PasswordStrengthMeterModule.forRoot(),
  ],
  entryComponents: [ConfirmDialog, SaveDialogComponent],
  providers: [
    CookieService,
    PendingChangesGuard,
    {
      provide: PSM_CONFIG,
      useValue: {
        translations: zxcvbnEnPackage.translations,
        graphs: zxcvbnCommonPackage.adjacencyGraphs,
        dictionary: {
          ...zxcvbnCommonPackage.dictionary,
          ...zxcvbnEnPackage.dictionary,
        },
      },
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
